import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Types
interface Sheet {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  sheetType: 'grid' | 'gantt' | 'card' | 'calendar';
  type?: 'grid' | 'gantt' | 'card' | 'calendar';
  position: number;
  isDefault: boolean;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  _count?: {
    tasks: number;
    columns: number;
  };
}

interface CreateSheetDto {
  name: string;
  description?: string;
  sheetType?: 'grid' | 'gantt' | 'card' | 'calendar';
  type?: 'grid' | 'gantt' | 'card' | 'calendar';
}

interface UpdateSheetDto {
  name?: string;
  description?: string;
  position?: number;
  settings?: Record<string, unknown>;
}

// Query keys
export const sheetKeys = {
  all: ['sheets'] as const,
  lists: () => [...sheetKeys.all, 'list'] as const,
  listByProject: (projectId: string) => [...sheetKeys.lists(), { projectId }] as const,
  detail: (id: string) => [...sheetKeys.all, 'detail', id] as const,
};

function normalizeSheet(sheet: Sheet): Sheet {
  if (!sheet.sheetType && sheet.type) {
    return { ...sheet, sheetType: sheet.type };
  }
  return sheet;
}

function extractSheetsFromResponse(payload: unknown): Sheet[] {
  if (Array.isArray(payload)) {
    return (payload as Sheet[]).map(normalizeSheet);
  }

  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.data)) {
      return (obj.data as Sheet[]).map(normalizeSheet);
    }

    if (Array.isArray(obj.sheets)) {
      return (obj.sheets as Sheet[]).map(normalizeSheet);
    }
  }

  return [];
}

// Fetch sheets for a project
export function useSheetsQuery(projectId: string) {
  return useQuery({
    queryKey: sheetKeys.listByProject(projectId),
    queryFn: async () => {
      const response = await api.get<unknown>(`/projects/${projectId}/sheets`);
      return extractSheetsFromResponse(response.data);
    },
    enabled: !!projectId,
  });
}

// Fetch single sheet
export function useSheetQuery(sheetId: string) {
  return useQuery({
    queryKey: sheetKeys.detail(sheetId),
    queryFn: async () => {
      const response = await api.get<Sheet>(`/sheets/${sheetId}`);
      return normalizeSheet(response.data);
    },
    enabled: !!sheetId,
  });
}

// Create sheet
export function useCreateSheet(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateSheetDto) => {
      const { type, ...rest } = dto;
      const payload = {
        ...rest,
        sheetType: dto.sheetType ?? type ?? 'grid',
      };
      const response = await api.post<Sheet>(`/projects/${projectId}/sheets`, payload);
      return normalizeSheet(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sheetKeys.listByProject(projectId) });
    },
  });
}

// Update sheet
export function useUpdateSheet(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sheetId, updates }: { sheetId: string; updates: UpdateSheetDto }) => {
      const response = await api.patch<Sheet>(`/sheets/${sheetId}`, updates);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sheetKeys.listByProject(projectId) });
      queryClient.invalidateQueries({ queryKey: sheetKeys.detail(data.id) });
    },
  });
}

// Delete sheet
export function useDeleteSheet(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sheetId: string) => {
      await api.delete(`/sheets/${sheetId}`);
      return sheetId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sheetKeys.listByProject(projectId) });
    },
  });
}

export type { Sheet, CreateSheetDto, UpdateSheetDto };
